import { observer } from 'mobx-react-lite';
import { StyleSheet, View } from 'react-native';

import { Input } from '@/components/Input';
import { spacing } from '@/constants/theme';
import { giftingStore } from '@/stores/gifting.store';

/**
 * The US shipping fields for the gift claim. Writes straight into the store's
 * shippingDraft (not local state) so the half-typed address survives a
 * session-expiry round trip through login — the draft is the one piece of
 * user work on this screen worth protecting.
 */
export const ShippingAddressForm = observer(() => {
  // Its own observer, not a plain child: the parent screen only tracks the
  // fields its completeness check reads, so a keystroke in street2 (which it
  // doesn't) would never re-render a non-observer child — a controlled input
  // whose value prop goes stale.
  const d = giftingStore.shippingDraft;

  return (
    <View style={styles.form}>
      <Input
        label="Full name"
        value={d.full_name}
        onChangeText={(t) => giftingStore.updateShippingDraft({ full_name: t })}
        autoCapitalize="words"
        autoComplete="name"
        textContentType="name"
      />
      <Input
        label="Street address"
        value={d.street}
        onChangeText={(t) => giftingStore.updateShippingDraft({ street: t })}
        autoCapitalize="words"
        autoComplete="street-address"
        textContentType="fullStreetAddress"
      />
      <Input
        label="Apt / unit (optional)"
        value={d.street2 ?? ''}
        onChangeText={(t) => giftingStore.updateShippingDraft({ street2: t })}
        autoCapitalize="words"
      />
      <View style={styles.row}>
        <View style={styles.city}>
          <Input
            label="City"
            value={d.city}
            onChangeText={(t) => giftingStore.updateShippingDraft({ city: t })}
            autoCapitalize="words"
            autoComplete="postal-address-locality"
            textContentType="addressCity"
          />
        </View>
        <View style={styles.state}>
          <Input
            label="State"
            value={d.state}
            onChangeText={(t) => giftingStore.updateShippingDraft({ state: t })}
            autoCapitalize="characters"
            autoCorrect={false}
            maxLength={2}
            placeholder="NY"
            textContentType="addressState"
          />
        </View>
        <View style={styles.zip}>
          <Input
            label="ZIP"
            value={d.zip}
            onChangeText={(t) => giftingStore.updateShippingDraft({ zip: t })}
            keyboardType="number-pad"
            autoComplete="postal-code"
            textContentType="postalCode"
            placeholder="10001"
          />
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  form: {
    gap: spacing.base,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  city: {
    flex: 2,
  },
  state: {
    flex: 1,
  },
  zip: {
    flex: 1.4,
  },
});
